import { useState } from "react";
import { api, ZEFFY } from "../../lib/api";
import { toast } from "sonner";
import { ArrowRight, Mail, Phone, FileText } from "lucide-react";

export function ContactInvoice() {
  const [tab, setTab] = useState("contact");
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 scroll-mt-nav"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <p className="text-overline text-aawta-pink mb-4">CONTACT</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-white tracking-tight leading-[1.05]">
            Let&rsquo;s talk.
          </h2>
          <p className="text-white/70 mt-6 leading-relaxed">
            For sponsorship, scholarships, media, or general inquiries about the 3rd Annual
            Power to Drive Change Fundraising Gala &mdash; reach out.
          </p>
          <div className="mt-10 space-y-4 text-white/85">
            <a href="mailto:contact@aawta.org" className="flex items-center gap-3 group" data-testid="contact-email">
              <Mail size={18} className="text-aawta-pink" />
              <span className="group-hover:text-aawta-pink transition">contact@aawta.org</span>
            </a>
            <a href="tel:+17707566768" className="flex items-center gap-3 group" data-testid="contact-phone">
              <Phone size={18} className="text-aawta-pink" />
              <span className="group-hover:text-aawta-pink transition">770-756-6768</span>
            </a>
          </div>

          <div className="mt-12">
            <p className="text-overline text-white/40 mb-3">WAYS TO GIVE</p>
            <a
              id="donate"
              href={ZEFFY.donate}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-donate-btn"
              className="btn-pink scroll-mt-nav w-full md:w-auto"
            >
              MAKE A DONATION VIA ZEFFY <ArrowRight size={14} />
            </a>
            <a
              href={ZEFFY.inKind}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-inkind-btn"
              className="btn-ghost mt-3 md:mt-0 md:ml-3 w-full md:w-auto"
            >
              IN-KIND / PRODUCT DONATION <ArrowRight size={14} />
            </a>
          </div>
        </div>

        <div className="md:col-span-7">
          <div className="flex border-b border-white/10 mb-6">
            <button
              data-testid="tab-contact"
              onClick={() => setTab("contact")}
              className={`px-5 py-3 text-overline transition-all ${
                tab === "contact" ? "text-aawta-pink border-b-2 border-aawta-pink" : "text-white/50 hover:text-white"
              }`}
            >
              GENERAL CONTACT
            </button>
            <button
              data-testid="tab-invoice"
              onClick={() => setTab("invoice")}
              className={`px-5 py-3 text-overline transition-all ${
                tab === "invoice" ? "text-aawta-pink border-b-2 border-aawta-pink" : "text-white/50 hover:text-white"
              }`}
            >
              REQUEST AN INVOICE
            </button>
          </div>
          {tab === "contact" ? <ContactForm /> : <InvoiceForm />}
        </div>
      </div>
    </section>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ full_name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received. We'll respond within 2 business days.");
      setForm({ full_name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Could not send message.");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <form onSubmit={submit} data-testid="contact-form" className="glass-panel p-8 md:p-10 space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="label-luxe">Name *</label>
          <input data-testid="ctc-name" required className="input-luxe" value={form.full_name} onChange={handle("full_name")} />
        </div>
        <div>
          <label className="label-luxe">Email *</label>
          <input data-testid="ctc-email" required type="email" className="input-luxe" value={form.email} onChange={handle("email")} />
        </div>
      </div>
      <div>
        <label className="label-luxe">Subject *</label>
        <input data-testid="ctc-subject" required className="input-luxe" value={form.subject} onChange={handle("subject")} />
      </div>
      <div>
        <label className="label-luxe">Message *</label>
        <textarea data-testid="ctc-message" required rows={5} className="input-luxe resize-none" value={form.message} onChange={handle("message")} />
      </div>
      <button type="submit" data-testid="ctc-submit" disabled={submitting} className="btn-pink w-full disabled:opacity-50">
        {submitting ? "SENDING..." : "SEND MESSAGE"} <ArrowRight size={14} />
      </button>
    </form>
  );
}

function InvoiceForm() {
  const [form, setForm] = useState({
    full_name: "", organization: "", email: "", phone: "",
    payment_type: "ACH Transfer", amount: "", purpose: "Sponsorship", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/invoice/request", form);
      toast.success("Invoice request received. Finance will email an invoice within 1 business day.");
      setForm({ ...form, full_name: "", organization: "", email: "", phone: "", amount: "", notes: "" });
    } catch {
      toast.error("Could not submit invoice request.");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <form onSubmit={submit} data-testid="invoice-form" className="glass-panel p-8 md:p-10 space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <FileText className="text-aawta-pink" size={20} />
        <p className="text-overline text-white/60">FOR ACH · CHECK · PROCUREMENT PAYMENTS</p>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="label-luxe">Contact Name *</label>
          <input data-testid="inv-name" required className="input-luxe" value={form.full_name} onChange={handle("full_name")} />
        </div>
        <div>
          <label className="label-luxe">Organization *</label>
          <input data-testid="inv-org" required className="input-luxe" value={form.organization} onChange={handle("organization")} />
        </div>
        <div>
          <label className="label-luxe">Email *</label>
          <input data-testid="inv-email" required type="email" className="input-luxe" value={form.email} onChange={handle("email")} />
        </div>
        <div>
          <label className="label-luxe">Phone</label>
          <input data-testid="inv-phone" className="input-luxe" value={form.phone} onChange={handle("phone")} />
        </div>
        <div>
          <label className="label-luxe">Payment Method *</label>
          <select data-testid="inv-method" className="input-luxe bg-aawta-jet" value={form.payment_type} onChange={handle("payment_type")}>
            <option>ACH Transfer</option>
            <option>Check</option>
            <option>Procurement / PO</option>
            <option>Wire Transfer</option>
          </select>
        </div>
        <div>
          <label className="label-luxe">Purpose *</label>
          <select data-testid="inv-purpose" className="input-luxe bg-aawta-jet" value={form.purpose} onChange={handle("purpose")}>
            <option>Sponsorship</option>
            <option>Scholarship Donation</option>
            <option>Strategic Partnership</option>
            <option>VIP Table</option>
            <option>General Donation</option>
            <option>Vendor Booth</option>
          </select>
        </div>
      </div>
      <div>
        <label className="label-luxe">Amount (USD) *</label>
        <input data-testid="inv-amount" required placeholder="e.g. 7500" className="input-luxe" value={form.amount} onChange={handle("amount")} />
      </div>
      <div>
        <label className="label-luxe">Billing Notes</label>
        <textarea data-testid="inv-notes" rows={3} className="input-luxe resize-none" value={form.notes} onChange={handle("notes")} />
      </div>
      <button type="submit" data-testid="inv-submit" disabled={submitting} className="btn-pink w-full disabled:opacity-50">
        {submitting ? "SENDING..." : "REQUEST INVOICE"} <ArrowRight size={14} />
      </button>
    </form>
  );
}
